const clone = value => value !== null && typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value;

const isEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const queryStore = {
    install(VueConstructor) {
        VueConstructor.prototype.$query = queryStore;
    },

    bind(vm, {
        property = 'options',
        namespace = '',
    } = {}) {
        const options = vm[property];
        const defaults = clone(options);
        const keys = Object.keys(defaults);

        // Restore before watching so reading the URL never writes back to it.
        this.restoreOptions(options, defaults, namespace);
        let previousOptions = clone(options);

        const unwatch = vm.$watch(
            property,
            value => {
                const changedKeys = keys.filter(
                    key => !isEqual(value[key], previousOptions[key]),
                );

                if (changedKeys.length) {
                    this.updateQuery(value, defaults, namespace, changedKeys);
                    previousOptions = clone(value);
                }
            },
            {deep: true},
        );

        vm.$once('hook:beforeDestroy', unwatch);
    },

    restoreOptions(options, defaults, namespace) {
        const query = new URLSearchParams(window.location.search);

        Object.entries(defaults).forEach(([key, defaultValue]) => {
            const value = query.get(this.key(namespace, key));

            options[key] = value === null
                ? clone(defaultValue)
                : this.parseValue(value, defaultValue);
        });
    },

    updateQuery(options, defaults, namespace, changedKeys) {
        const url = new URL(window.location.href);

        changedKeys.forEach(key => {
            const value = options[key];
            const name = this.key(namespace, key);
            const isEmpty = value === null || value === undefined || value === '';

            if (isEmpty || isEqual(value, defaults[key])) {
                url.searchParams.delete(name);
            } else {
                url.searchParams.set(
                    name,
                    typeof value === 'object' ? JSON.stringify(value) : String(value),
                );
            }
        });

        if (url.href !== window.location.href) {
            window.history.replaceState(window.history.state, '', url);
        }
    },

    // URLSearchParams returns strings, so the default determines the target type.
    parseValue(value, defaultValue) {
        if (typeof defaultValue === 'number') {
            const parsed = Number(value);

            return Number.isFinite(parsed) ? parsed : defaultValue;
        }

        if (typeof defaultValue === 'boolean') {
            if (value === 'true' || value === '1') return true;
            if (value === 'false' || value === '0') return false;

            return defaultValue;
        }

        if (defaultValue !== null && typeof defaultValue === 'object') {
            try {
                const parsed = JSON.parse(value);

                if (Array.isArray(defaultValue)) {
                    return Array.isArray(parsed) ? parsed : defaultValue;
                }

                const isObject = parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed);

                return isObject ? {...defaultValue, ...parsed} : defaultValue;
            } catch {
                return defaultValue;
            }
        }

        return value;
    },

    key(namespace, key) {
        return namespace ? `${namespace}_${key}` : key;
    },
};

export default queryStore;
